import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createProject } from '../../services/firestoreService';
import { useNavigate } from 'react-router-dom';

export default function ProjectForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    budgetRange: '',
    timeline: '',
    skills: '',
    projectType: 'One-time',
    urgency: 'Medium',
    deliverables: '',
    contactEmail: '',
    externalUrl: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const projectData = {
        ...formData,
        skills: formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill),
        deliverables: formData.deliverables.split('\n').filter(deliverable => deliverable.trim()),
        contactEmail: formData.contactEmail || currentUser.email
      };

      const projectId = await createProject(projectData, currentUser.uid);
      navigate(`/projects/${projectId}`);
    } catch (error) {
      setError('Failed to create project posting. Please try again.');
      console.error('Error creating project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Post a New Project</h2>
          <p className="mt-2 text-gray-600">
            Find talented professionals for your project needs.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Project Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="e.g., Financial Model Development for Startup"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Project Description *
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="6"
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="Describe your project, goals, and what you're looking for..."
            />
          </div>

          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="budgetRange" className="block text-sm font-medium text-gray-700">
                Budget Range *
              </label>
              <select
                id="budgetRange"
                name="budgetRange"
                required
                value={formData.budgetRange}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              >
                <option value="">Select budget range</option>
                <option value="Under $1,000">Under $1,000</option>
                <option value="$1,000 - $5,000">$1,000 - $5,000</option>
                <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                <option value="$50,000+">$50,000+</option>
                <option value="To be discussed">To be discussed</option>
              </select>
            </div>

            <div>
              <label htmlFor="timeline" className="block text-sm font-medium text-gray-700">
                Timeline *
              </label>
              <select
                id="timeline"
                name="timeline"
                required
                value={formData.timeline}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              >
                <option value="">Select timeline</option>
                <option value="Less than 1 month">Less than 1 month</option>
                <option value="1-3 months">1-3 months</option>
                <option value="3-6 months">3-6 months</option>
                <option value="6-12 months">6-12 months</option>
                <option value="More than 1 year">More than 1 year</option>
                <option value="Ongoing">Ongoing</option>
              </select>
            </div>

            <div>
              <label htmlFor="projectType" className="block text-sm font-medium text-gray-700">
                Project Type *
              </label>
              <select
                id="projectType"
                name="projectType"
                required
                value={formData.projectType}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              >
                <option value="One-time">One-time project</option>
                <option value="Ongoing">Ongoing work</option>
                <option value="Contract">Contract position</option>
                <option value="Consulting">Consulting engagement</option>
              </select>
            </div>

            <div>
              <label htmlFor="urgency" className="block text-sm font-medium text-gray-700">
                Urgency Level *
              </label>
              <select
                id="urgency"
                name="urgency"
                required
                value={formData.urgency}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              >
                <option value="Low">Low - Flexible timeline</option>
                <option value="Medium">Medium - Standard timeline</option>
                <option value="High">High - Need to start soon</option>
                <option value="Urgent">Urgent - Need to start immediately</option>
              </select>
            </div>
          </div>

          {/* Skills Required */}
          <div>
            <label htmlFor="skills" className="block text-sm font-medium text-gray-700">
              Skills Required *
            </label>
            <input
              type="text"
              id="skills"
              name="skills"
              required
              value={formData.skills}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="e.g., Financial Modeling, Excel, Python, Data Analysis"
            />
            <p className="mt-1 text-sm text-gray-500">Separate skills with commas</p>
          </div>

          {/* Deliverables */}
          <div>
            <label htmlFor="deliverables" className="block text-sm font-medium text-gray-700">
              Expected Deliverables
            </label>
            <textarea
              id="deliverables"
              name="deliverables"
              rows="4"
              value={formData.deliverables}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="Enter each deliverable on a new line..."
            />
            <p className="mt-1 text-sm text-gray-500">Enter each deliverable on a separate line</p>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                Contact Email
              </label>
              <input
                type="email"
                id="contactEmail"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="Leave blank to use your email"
              />
            </div>

            <div>
              <label htmlFor="externalUrl" className="block text-sm font-medium text-gray-700">
                External Project URL
              </label>
              <input
                type="url"
                id="externalUrl"
                name="externalUrl"
                value={formData.externalUrl}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="https://company.com/project-details"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Posting...' : 'Post Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
